/**
 * Read-through cache for what the POS needs to keep working without the cloud (08-clients.md
 * "State": bootstrap bundle, menu + prices + tax rules, tables, open orders): every read that
 * succeeds is written to the offline store; when the cloud cannot be reached the last copy is
 * answered instead, so a screen never turns into an error just because the network went.
 */
import { isNetworkError } from '@qrkey/api-client';
import { connectivity } from './connectivity.js';
import { kvGet, kvSet } from './db.js';

export const keys = {
  me: (userId: string) => `me:${userId}`,
  bootstrap: (outletId: string, userId: string) => `bootstrap:${outletId}:${userId}`,
  bootstrapLatest: (outletId: string) => `bootstrap:${outletId}:latest`,
  menu: (outletId: string, context: string, sectionId: string | null) =>
    `menu:${outletId}:${context}:${sectionId ?? ''}`,
  menuPrefix: (outletId: string) => `menu:${outletId}:`,
  tables: (outletId: string) => `tables:${outletId}`,
  board: (outletId: string) => `board:${outletId}`,
  order: (outletId: string, orderId: string) => `order:${outletId}:${orderId}`,
  payments: (outletId: string, orderId: string) => `payments:${outletId}:${orderId}`,
  localPayments: (outletId: string, orderId: string) => `local-payments:${outletId}:${orderId}`,
  invoice: (outletId: string, orderId: string) => `invoice:${outletId}:${orderId}`,
  orderCounter: (outletId: string) => `local-order-no:${outletId}`,
} as const;

/**
 * `fetcher()` and remember it under `key`; when the network is down (or the app already
 * knows it is) answer the remembered copy. Problems the server answered (a 403, a 404) are
 * answers and pass through. Without a remembered copy the network error passes through.
 */
export async function cachedRead<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = connectivity.isOnline() ? undefined : await kvGet<T>(key);
  if (cached !== undefined) return cached;
  try {
    const value = await fetcher();
    connectivity.reportAnswer();
    void kvSet(key, value);
    return value;
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    connectivity.reportFailure();
    const fallback = await kvGet<T>(key);
    if (fallback !== undefined) return fallback;
    throw error;
  }
}
